export type FinanceNewsTopic = 'all' | 'macro' | 'energy' | 'companies' | 'geopolitics';

export type LiveNewsArticle = {
  id: string;
  title: string;
  url: string;
  domain: string;
  seenAt: string;
  language: string;
  sourceCountry: string;
  provider: 'GDELT' | 'Google News RSS';
};

const searches: Record<FinanceNewsTopic, string> = {
  all: '("Federal Reserve" OR inflation OR "interest rates" OR stock market OR bonds OR oil OR earnings OR tariff)',
  macro: '("Federal Reserve" OR inflation OR "interest rates" OR Treasury OR "central bank")',
  energy: '(oil OR crude OR OPEC OR "natural gas" OR energy)',
  companies: '(earnings OR stocks OR shares OR company OR semiconductor)',
  geopolitics: '(tariff OR sanctions OR conflict OR Iran OR trade)',
};

const fallbackSearches: Record<FinanceNewsTopic, string> = {
  all: 'finance OR markets',
  macro: 'Federal Reserve inflation interest rates Treasury central bank',
  energy: 'oil crude OPEC natural gas energy',
  companies: 'earnings stocks shares companies semiconductor',
  geopolitics: 'tariffs sanctions conflict Iran trade',
};

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function requestDeadline(signal?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(12000);
  return signal ? AbortSignal.any([signal, timeout]) : timeout;
}

export function parseGdeltArticles(payload: unknown): LiveNewsArticle[] {
  if (!payload || typeof payload !== 'object' || !('articles' in payload) || !Array.isArray(payload.articles)) return [];
  const seen = new Set<string>();
  return payload.articles.flatMap((raw): LiveNewsArticle[] => {
    if (!raw || typeof raw !== 'object') return [];
    const row = raw as Record<string, unknown>;
    const title = text(row.title);
    const url = text(row.url);
    let parsedUrl: URL;
    try { parsedUrl = new URL(url); } catch { return []; }
    if (!title || !['http:', 'https:'].includes(parsedUrl.protocol) || seen.has(url)) return [];
    seen.add(url);
    return [{
      id: url,
      title,
      url,
      domain: text(row.domain) || parsedUrl.hostname,
      seenAt: text(row.seendate),
      language: text(row.language),
      sourceCountry: text(row.sourceCountry) || text(row.sourcecountry),
      provider: 'GDELT',
    }];
  });
}

export function parseGoogleNewsRss(payload: unknown): LiveNewsArticle[] {
  if (!payload || typeof payload !== 'object') return [];
  const data = payload as Record<string, unknown>;
  if (data.status !== 'ok' || !Array.isArray(data.items)) return [];
  const seen = new Set<string>();
  return data.items.flatMap((raw): LiveNewsArticle[] => {
    if (!raw || typeof raw !== 'object') return [];
    const row = raw as Record<string, unknown>;
    const title = text(row.title);
    const url = text(row.link);
    let parsedUrl: URL;
    try { parsedUrl = new URL(url); } catch { return []; }
    if (!title || !['http:', 'https:'].includes(parsedUrl.protocol) || seen.has(url)) return [];
    seen.add(url);
    return [{
      id: url,
      title,
      url,
      domain: text(row.author) || 'Google News',
      seenAt: text(row.pubDate),
      language: 'English search · original headline may vary',
      sourceCountry: '',
      provider: 'Google News RSS',
    }];
  });
}

async function fetchGoogleNews(topic: FinanceNewsTopic, signal: AbortSignal): Promise<LiveNewsArticle[]> {
  const feed = new URL('https://news.google.com/rss/search');
  feed.searchParams.set('q', `${fallbackSearches[topic]} when:1d`);
  feed.searchParams.set('hl', 'en-US');
  feed.searchParams.set('gl', 'US');
  feed.searchParams.set('ceid', 'US:en');
  const proxy = new URL('https://api.rss2json.com/v1/api.json');
  proxy.searchParams.set('rss_url', feed.toString());
  const response = await fetch(proxy, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`RSS fallback returned HTTP ${response.status}`);
  const articles = parseGoogleNewsRss(await response.json());
  if (!articles.length) throw new Error('RSS fallback returned no readable headlines');
  return articles;
}

async function fetchGdelt(topic: FinanceNewsTopic, signal: AbortSignal): Promise<LiveNewsArticle[]> {
  const params = new URLSearchParams({
    query: searches[topic],
    mode: 'ArtList',
    format: 'json',
    timespan: '24h',
    maxrecords: '50',
    sort: 'DateDesc',
  });
  const response = await fetch(`https://api.gdeltproject.org/api/v2/doc/doc?${params}`, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`GDELT returned HTTP ${response.status}`);
  const articles = parseGdeltArticles(await response.json());
  if (!articles.length) throw new Error('GDELT returned no readable headlines');
  return articles;
}

export async function fetchFinanceNews(topic: FinanceNewsTopic, signal?: AbortSignal): Promise<LiveNewsArticle[]> {
  try {
    return await fetchGoogleNews(topic, requestDeadline(signal));
  } catch (primaryError) {
    if (signal?.aborted) throw primaryError;
    try {
      return await fetchGdelt(topic, requestDeadline(signal));
    } catch (fallbackError) {
      if (signal?.aborted) throw fallbackError;
      const primaryMessage = primaryError instanceof Error ? primaryError.message : 'Unknown error';
      const fallbackMessage = fallbackError instanceof Error ? fallbackError.message : 'Unknown error';
      throw new Error(`Google News RSS: ${primaryMessage}. GDELT fallback: ${fallbackMessage}`);
    }
  }
}
